import { get } from 'lodash-es'

import { OPERA_URL } from '@/main'
import {
  compareValuesNullsLast,
  parsePrimaryKeyAndSource,
  getChemicalMassFromParams,
} from '@/components/shared-table/utils'

import {
  buildColumnDef,
  getFormattedValue,
  getModelPredictionRoot,
  formatValueWithConversion,
  getChemicalFromExtras,
  isPredictedField,
} from '@/components/shared-table/buildColumnDef'

import { categoricalEndpoints, getCategoryLabelByValue } from '@/components/combined-predict/models/opera/operaMetadata'

const getLink = (operaModelId, chemical) => {
  if (!operaModelId || !chemical) return null

  const chemId = chemical.sid
  if (!chemId) return null
  return `${OPERA_URL}/report/?dtxsid=${chemId}&modelId=${operaModelId}`
}

const findPredictionContextByFieldKey = ({ modelId, predictionsByModelId, fieldKey, extrasByModelId }) => {
  const predictionForModel = getModelPredictionRoot(predictionsByModelId, modelId)

  if (!predictionForModel?.length) return null

  const { sourceChemical } = getChemicalFromExtras(extrasByModelId, modelId)

  const { primaryKey: endpointId, sourceType } = parsePrimaryKeyAndSource(fieldKey, modelId)

  const endpointResult = predictionForModel.find((prediction) => prediction?.endpoint_id === endpointId)
  const sourceResult = get(endpointResult, sourceType, null)

  return { endpointResult, sourceResult, link: getLink(endpointResult?.model_id, sourceChemical) }
}

const getPredictionRawValue = (predictionContext) => {
  const { endpointResult, sourceResult } = predictionContext || {}
  const endpointId = endpointResult?.endpoint_id

  if (categoricalEndpoints.includes(endpointId)) {
    return getCategoryLabelByValue(sourceResult?.value)
  }

  const value = sourceResult?.value

  if (value === false) return 'No'
  if (value === true) return 'Yes'

  return value ?? null
}

const getTooltipPrediction = ({ column, params }) => {
  const { endpointResult } =
    findPredictionContextByFieldKey({
      modelId: column?.modelId,
      predictionsByModelId: params?.data?.predictionsByModelId,
      fieldKey: params?.colDef?.field,
    }) ?? {}

  if (!endpointResult) return null

  return {
    unit: column?.unit,
    endpointResult,
  }
}

const buildOperaColumnDef = ({ column, visibleColumnIds, tableSettings }) =>
  buildColumnDef({
    column,
    visibleColumnIds,
    tableSettings,

    tooltipComponent: 'OperaTooltip',
    tooltipComponentParams: (params) => ({
      prediction: getTooltipPrediction({ column, params }),
    }),

    onCellClicked: (params) => {
      const link = params?.value?.link

      if (!link) return

      window.open(link, '_blank', 'noopener,noreferrer')
    },

    ...(isPredictedField(column?.id)
      ? {
          cellClassRules: {
            'text-warning': (params) => {
              const { sourceResult } =
                findPredictionContextByFieldKey({
                  modelId: column?.modelId,
                  predictionsByModelId: params?.data?.predictionsByModelId,
                  fieldKey: params?.colDef?.field,
                }) ?? {}
              return sourceResult?.ad !== 1
            },
          },
        }
      : {}),

    valueGetter: (params) =>
      findPredictionContextByFieldKey({
        modelId: column?.modelId,
        predictionsByModelId: params?.data?.predictionsByModelId,
        fieldKey: params?.colDef?.field,
        extrasByModelId: params?.data?.extrasByModelId,
      }),

    valueFormatter: (params) =>
      getFormattedValue({
        params,
        column,
        formatFunction: () => {
          const rawValue = getPredictionRawValue(params?.value)
          return formatValueWithConversion({
            value: rawValue,
            fromUnit: column?.originalUnit,
            toUnit: column?.unit,
            mass: getChemicalMassFromParams(params),
          })
        },
      }),

    comparator: (aValue, bValue, _nodeA, _nodeB, isDescending) =>
      compareValuesNullsLast(getPredictionRawValue(aValue), getPredictionRawValue(bValue), isDescending),
  })

export { buildOperaColumnDef, findPredictionContextByFieldKey, getPredictionRawValue }
